import React from 'react';
import { CheckCircle, AlertCircle, Clock, TrendingUp, ChevronRight } from 'lucide-react';

const GapAnalysisSummary = ({ gapAnalysis, onViewDetails }) => {
  if (!gapAnalysis) {
    return null;
  }

  const {
    total_controls = 0,
    verified_controls = 0,
    pending_controls = 0,
    progress_percentage = 0,
    tasks_by_status = {},
    by_control_category = {},
    high_priority_remaining = [],
  } = gapAnalysis;

  const actionsRemaining = pending_controls + (tasks_by_status.failed || 0);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Gap Analysis Complete</h3>
          <CheckCircle className="w-8 h-8 opacity-80" />
        </div>

        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-3xl font-bold">{verified_controls}</p>
            <p className="text-sm text-teal-100">of {total_controls} controls verified</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{Math.round(progress_percentage)}%</p>
            <p className="text-sm text-teal-100">automatically verified</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{actionsRemaining}</p>
            <p className="text-sm text-teal-100">actions remaining</p>
          </div>
        </div>
      </div>

      {/* Progress by Category */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Progress by Control Category</h4>

        <div className="space-y-4">
          {Object.entries(by_control_category).map(([category, data]) => {
            const progress = data.progress_percentage || 0;

            return (
              <div key={category}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{category}</span>
                  <span className="text-sm text-gray-500">
                    {data.completed_tasks}/{data.total_tasks} tasks
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      progress >= 100
                        ? 'bg-green-500'
                        : progress >= 50
                          ? 'bg-teal-500'
                          : 'bg-amber-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task Status Breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatusCard
          label="Auto Verified"
          count={tasks_by_status.auto_verified || 0}
          icon={<CheckCircle className="w-5 h-5 text-green-500" />}
          bgColor="bg-green-50"
        />
        <StatusCard
          label="Manually Verified"
          count={tasks_by_status.manually_verified || 0}
          icon={<CheckCircle className="w-5 h-5 text-blue-500" />}
          bgColor="bg-blue-50"
        />
        <StatusCard
          label="Pending"
          count={tasks_by_status.pending || 0}
          icon={<Clock className="w-5 h-5 text-amber-500" />}
          bgColor="bg-amber-50"
        />
        <StatusCard
          label="Failed"
          count={tasks_by_status.failed || 0}
          icon={<AlertCircle className="w-5 h-5 text-red-500" />}
          bgColor="bg-red-50"
        />
      </div>

      {/* High Priority Tasks */}
      {high_priority_remaining.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              High Priority Actions
            </h4>
            <span className="text-sm text-gray-500">
              {high_priority_remaining.length} remaining
            </span>
          </div>

          <div className="space-y-3">
            {high_priority_remaining.slice(0, 5).map((task) => (
              <div
                key={task.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono bg-gray-200 px-2 py-1 rounded">
                    {task.control_id}
                  </span>
                  <span className="text-sm text-gray-900">{task.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      task.priority === 'critical'
                        ? 'bg-red-100 text-red-700'
                        : task.priority === 'high'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {task.priority}
                  </span>
                  {task.auto_checkable && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">
                      Auto-checkable
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {high_priority_remaining.length > 5 && (
            <button
              onClick={onViewDetails}
              className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              View all {high_priority_remaining.length} high priority tasks
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Next Steps */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-teal-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-teal-900 mb-2">Next Steps</h4>
            <ul className="text-sm text-teal-800 space-y-1">
              <li>1. Review and address high priority tasks first</li>
              <li>2. Connect any skipped integrations for automated verification</li>
              <li>3. Gather evidence for manual verification tasks</li>
              <li>4. Schedule regular rescans to track progress</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Card Component
const StatusCard = ({ label, count, icon, bgColor }) => (
  <div className={`${bgColor} rounded-lg p-4`}>
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-900">{count}</p>
  </div>
);

export default GapAnalysisSummary;
